'use client';

import React, { useState } from 'react';
import AddressInputV2, { AddressDataV2 as AddressData } from '@/components/auth/AddressInputV2';

type RequestAssistanceModalProps = {
    open: boolean;
    onClose: () => void;
    organisationId: string;
};

export default function RequestAssistanceModal({ open, onClose, organisationId }: RequestAssistanceModalProps) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
    phone: '',
    message: '',
    address: {
      address: '',
      city: '',
      state: '',
      zip: '',
      country: 'United States',
      fullAddress: '',
    } as AddressData,
  });

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
    // Address follows the same pattern as RegisterForm
    if (!open) return null;

    return (
        <>
            {/* Semi-transparent backdrop */}
            <div className="modal-backdrop-custom" aria-hidden="true"></div>

            <div className="modal fade show" id="signupModal" tabIndex={-1} aria-labelledby="signupModalLabel" aria-modal="true" role="dialog" style={{ display: 'block' }}>
                <div className="modal-dialog modal-fullscreen modal-animate">
                    <div className="modal-content modal-content-animate">
                        <div className="modal-body p-0">
                            <section className="signup-sec">
                                <div className="container">
                                    <div className="row">
                                        <div className="col-lg-8 col-md-8 col-11 m-auto">
                                            <div className="signup-box">
                                                <div className="popup-top d-flex justify-content-between align-items-center mb-4">
                                                    <h2>Request Assistance</h2>
                                                    <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
                                                </div>

                                                <div className="d-flex popup-box align-items-center justify-content-between mb-lg-4 mb-xl-5">
                                                    <div className="heading">
                                                        <h5>Request Services:</h5>
                                                        <p>
                                                            Direct Connect is a secure referral platform operated by No Wrong Door Virginia.
                                                            Please provide your preferred contact information.
                                                        </p>
                                                    </div>
                                                    <div className="popup-logo">
                                                        <img src="/images/logos/direct-logo.png" alt="Direct Connect" />
                                                    </div>
                                                </div>

                                                <form
                                                    action="#"
                                                    onSubmit={async (e) => {
                                                        e.preventDefault();
                                                        setSubmitError(null);
                                                        setSubmitSuccess(null);
                                                        if (formData.email !== formData.confirmEmail) {
                                                            setSubmitError('Emails do not match');
                                                            return;
                                                        }
                                                        try {
                                                            setSubmitting(true);
                                                            const backendUrl = (process.env.NEXT_PUBLIC_API_BACKEND || 'http://localhost:8000').replace(/\/$/, '');
                                                            const url = backendUrl.endsWith('/api/v1')
                                                                ? `${backendUrl}/contact`
                                                                : `${backendUrl}/api/v1/contact`;
                                                            const payload = {
                                                                firstName: formData.firstName,
                                                                lastName: formData.lastName,
                                                                email: formData.email,
                                                                phone: formData.phone,
                                                                address: {
                                                                    address: formData.address?.address || formData.address?.fullAddress || '',
                                                                    city: formData.address?.city || '',
                                                                    state: formData.address?.state || '',
                                                                    zip: formData.address?.zip || '',
                                                                    country: formData.address?.country || 'United States',
                                                                    fullAddress: formData.address?.fullAddress || formData.address?.address || '',
                                                                },
                                                                organisationId,
                                                                message: formData.message,
                                                            };
                                                            const response = await fetch(url, {
                                                                method: 'POST',
                                                                headers: {
                                                                    'Content-Type': 'application/json',
                                                                    'Origin': typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000',
                                                                },
                                                                body: JSON.stringify(payload),
                                                            });
                                                            if (!response.ok) {
                                                                let friendly = `Request failed: ${response.status}`;
                                                                try {
                                                                    const errJson = await response.json();
                                                                    if (errJson?.message) friendly = errJson.message;
                                                                    if (errJson?.errors?.length) friendly = errJson.errors[0]?.message || friendly;
                                                                    console.error('Contact submit error JSON:', errJson);
                                                                } catch (_) {
                                                                    const text = await response.text();
                                                                    console.error('Contact submit error text:', text);
                                                                }
                                                                throw new Error(friendly);
                                                            }
                                                            setSubmitSuccess('Request sent successfully');
                                                            onClose();
                                                        } catch (err: any) {
                                                            setSubmitError(err?.message || 'Failed to send request');
                                                        } finally {
                                                            setSubmitting(false);
                                                        }
                                                    }}
                                                >
                                                    <div className="row">
                                                        <div className="col-lg-6">
                                                            <label>First Name <span>*</span></label>
                                                            <input placeholder="Enter your first name" type="text" className="form-control" required value={formData.firstName} onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))} />
                                                        </div>
                                                        <div className="col-lg-6">
                                                            <label>Last Name <span>*</span></label>
                                                            <input placeholder="Enter your last name" type="text" className="form-control" required value={formData.lastName} onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))} />
                                                        </div>
                                                        <div className="col-lg-12">
                                                            <label>Address </label>
                                                            <AddressInputV2
                                                                placeholder="Enter address"
                                                                value={formData.address?.fullAddress || ''}
                                                                onChange={(addressString) => {
                                                                    // Only update the fullAddress for display, don't overwrite structured data
                                                                    if (formData.address && typeof formData.address === 'object') {
                                                                        const newValue = { 
                                                                            ...formData.address, 
                                                                            fullAddress: addressString
                                                                        } as AddressData;
                                                                        setFormData(prev => ({ ...prev, address: newValue }));
                                                                    } else {
                                                                        const newValue = { 
                                                                            address: addressString,
                                                                            fullAddress: addressString
                                                                        } as AddressData;
                                                                        setFormData(prev => ({ ...prev, address: newValue }));
                                                                    }
                                                                }}
                                                                onAddressSelect={(addr) => {
                                                                    // Update the structured address data - this should contain all fields
                                                                    setFormData(prev => ({ ...prev, address: addr }));
                                                                }}
                                                                required
                                                            />
                                                        </div>

                                                        <div className="col-lg-6 mb-3">
                                                            <label>Email <span>*</span></label>
                                                            <input type="email" className="form-control" placeholder="Email*" required value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} />
                                                        </div>
                                                        <div className="col-lg-6 mb-3">
                                                            <label>Confirm Email <span>*</span></label>
                                                            <input type="email" className="form-control" placeholder="Confirm Email*" required value={formData.confirmEmail} onChange={(e) => setFormData(prev => ({ ...prev, confirmEmail: e.target.value }))} />
                                                        </div>
                                                        <div className="col-lg-6 mb-3">
                                                            <label>Phone <span>*</span></label>
                                                            <input type="tel" className="form-control" placeholder="Phone*" required value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} />
                                                        </div>
                                                        <div className="col-lg-12">
                                                            <div className="note">
                                                                <h5>Optional Information:</h5>
                                                                <p>
                                                                    Please share some additional information about the person who will be receiving services.
                                                                </p>
                                                                <span>
                                                                    <textarea
                                                                        className="form-textarea form-control w-full"
                                                                        name="message"
                                                                        rows={4}
                                                                        maxLength={512}
                                                                        placeholder={"Share any information that may help providers understand what you need. For example: 'I'm looking for a care provider from 8 a.m. to 3 p.m. on weekends.' Maximum length is 512 characters. Please do not use these three special characters: > < &"}
                                                                        value={formData.message}
                                                                        onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                                                                    ></textarea>
                                                                </span>
                                                                <p>
                                                                    Your information will only be shared with the service provider(s) you chose. By clicking "Send" you agree to our <a href="#">Terms of Use</a> and <a href="#">Privacy Policy</a>
                                                                </p>
                                                                <p>This question is for testing whether or not you are a human visitor and to prevent automated spam submissions</p>
                                                            </div>
                                                            <div className="col-lg-12">
                                                                <button type="submit" className="btn btn-primary" disabled={submitting}>
                                                                    {submitting ? (
                                                                        <>
                                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                                            Sending...
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <img src="/images/icons/drop_down.svg" alt="" /> Send
                                                                        </>
                                                                    )}
                                                                </button>
                                                            </div>
                                                            {submitError && (
                                                                <div className="col-lg-12 mt-3">
                                                                    <div className="alert alert-danger" role="alert">
                                                                        {submitError}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {submitSuccess && (
                                                                <div className="col-lg-12 mt-3">
                                                                    <div className="alert alert-success" role="alert">
                                                                        {submitSuccess}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </form>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </div>

            {/* Transitions and backdrop styles */}
            <style jsx>{`
      .modal-backdrop-custom {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.5); /* transparent dark overlay */
        z-index: 1040; /* below modal (1050) */
        animation: fadeIn 200ms ease-out both;
      }
      :global(.modal), :global(.modal-dialog), :global(.modal-content) {
        overflow: visible !important;
      }
      .modal-animate {
        animation: fadeIn 200ms ease-out both;
      }
      .modal-content-animate {
        animation: slideUp 220ms ease-out both;
        background: transparent !important; /* transparent modal surface */
        border: 0;
        box-shadow: none;
        overflow: visible; /* allow autocomplete dropdown to render */
      }
      /* Ensure inner section doesn't force white background */
      :global(.signup-sec) {
        background: transparent !important;
      }
      /* Ensure Google Places suggestion dropdown appears above modal */
      :global(.pac-container) {
        z-index: 20000 !important;
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes slideUp {
        from { transform: translateY(12px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
    `}</style>
        </>
    );
}


